/**
 * The chrome around every page.
 *
 * Navigation is built from the route registry, not from a list written here,
 * and each entry is filtered by the same `requirement` the router's access
 * gate reads. That is what makes the menu and the router agree by
 * construction: a link cannot appear for a page the user would be refused,
 * because both read one declaration. How the entries are arranged into the
 * rail's areas is `navigation.tsx`.
 *
 * Hiding a link is not access control. The server re-authorises every request;
 * this only spares the user from discovering a refusal by being refused.
 */

import { Link, Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAccess } from "@hardauto/core-access";
import { navigationFor, type RouteDefinition } from "@hardauto/core-router";
import { AppShell, Button, useTheme } from "@hardauto/core-ui";
import { NOTIFICATION_PATHS, NotificationBell } from "@hardauto/feature-notifications";
import { useSession } from "@hardauto/feature-iam";

import { client } from "./api";
import brandLogo from "./assets/brand-logo.png";
import { LanguageSwitcher } from "./language-switcher";
import { RailNavigation } from "./navigation";
import { ContextBar } from "./workspace";
import { routeTable } from "./routes";

/**
 * Two letters for an avatar.
 *
 * From a display name where there is one, from the address otherwise. Purely
 * decorative — the name itself is beside it, and this carries `aria-hidden`,
 * because "YB" announced to a screen reader is noise.
 */
function initials(from: string): string {
  const parts = from
    .trim()
    .split(/[\s@._-]+/u)
    .filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0] ?? "");
  return letters.join("").toUpperCase() || "?";
}

export function AppLayout() {
  const { t } = useTranslation();
  const session = useSession();
  const { snapshot, ready } = useAccess();
  const { preference, setPreference } = useTheme();

  /** Whether the current principal may reach a route. */
  const permitted = (route: RouteDefinition): boolean => {
    const required = route.requirement?.permissions ?? [];
    if (required.length === 0) return true;
    // Fail closed while permissions load: a link that appears and then
    // disappears is worse than one that appears a moment late.
    if (!ready) return false;
    return route.requirement?.mode === "any"
      ? snapshot.canAnyOf(required)
      : snapshot.canAllOf(required);
  };

  const main = navigationFor(routeTable, "main").filter(permitted);
  const admin = navigationFor(routeTable, "admin").filter(permitted);
  const account = navigationFor(routeTable, "account").filter(permitted);
  // "Mon compte": the first account screen, reached from the name.
  const accountPage = account.find((route) => route.path !== NOTIFICATION_PATHS.list);

  // The public shell: no navigation, no account menu. Sign-in and the error
  // pages render inside it, and giving them a sidebar full of links they
  // cannot use would be worse than giving them none.
  if (session.status !== "authenticated") {
    return (
      <div className="hardauto-public-shell">
        <Outlet />
      </div>
    );
  }

  return (
    <AppShell
      skipToContentLabel={t("common:nav.skipToContent")}
      navigationLabel={t("common:nav.mainNavigation")}
      menuLabel={t("backoffice:shell.menu")}
      header={
        <div className="hardauto-header">
          {/* The brand lives on the rail, as the design draws it. The top bar
              carries the working context — for whom, which company, which
              stock, which period — because it changes what every page shows,
              and a reader should never have to wonder which one they are in. */}
          <ContextBar />
          <div className="hardauto-header__actions">
            {/* The bell is composed here rather than inside `AppShell`,
                because the shell is a `core-*` package and must not know that
                notifications exist. This file is where a feature and the
                chrome are allowed to meet. */}
            <NotificationBell client={client} href={NOTIFICATION_PATHS.list} />

            <LanguageSwitcher />

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPreference(preference === "dark" ? "light" : "dark");
              }}
              // The control changes the theme; its label must say which theme
              // it will switch TO, not which one is active — a button labelled
              // with the current state reads as a status, not an action.
              aria-label={t(preference === "dark" ? "common:theme.light" : "common:theme.dark")}
            >
              {preference === "dark" ? "☀" : "☾"}
            </Button>
          </div>
        </div>
      }
      sidebar={
        <>
          {/* The installation's logo, which carries its name and its line; the
              alt text says the name for whoever cannot see it. */}
          <Link to="/" className="hardauto-brand hardauto-brand--logo">
            <img className="hardauto-brand__logo" src={brandLogo} alt={t("common:appName")} />
            <span className="hardauto-brand__subtitle">{t("common:appTagline")}</span>
          </Link>

          {/* Five spaces. The account screens are personal rather than a
              space, so they live with the name at the bottom of the rail. */}
          <RailNavigation entries={[...main, ...admin]} />

          {/* Who is signed in, and the way out. At the bottom of the rail
              because it is the least-used control on the screen and the most
              consequential — the design puts it there for the same reason. */}
          <div className="hardauto-rail-account">
            <div className="hardauto-rail-account__who">
              <span className="hardauto-rail-account__initials" aria-hidden="true">
                {initials(session.user?.display_name ?? session.user?.email ?? "?")}
              </span>
              <span style={{ minInlineSize: 0 }}>
                {accountPage ? (
                  <Link to={accountPage.path} className="hardauto-rail-account__name">
                    {session.user?.display_name ?? session.user?.email ?? ""}
                  </Link>
                ) : (
                  <span className="hardauto-rail-account__name">
                    {session.user?.display_name ?? session.user?.email ?? ""}
                  </span>
                )}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void session.signOut();
              }}
            >
              {t("iam:signOut")}
            </Button>
          </div>
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}
